import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import {
  clearPayplusPendingCheckout,
  pollBookingUntilPaymentConfirmed,
  type PayplusReturnOutcome,
} from '../utils/payplusPayment';
import styles from './BookingPaymentReturnPanel.module.css';

type BookingPaymentReturnPanelProps = {
  outcome: PayplusReturnOutcome;
  bookingId: string | null;
  onDone: (opts: { bookingId: string | null; confirmed: boolean }) => void;
};

/**
 * After PayPlus browser redirect — UX only. Confirmed payment = GET booking status_id === 2.
 * On confirmed, automatically hands off to the booking confirmation (QR) card.
 * Cancel/failure also auto-hand off so callers can restore the asset (like Bit/credits cancel).
 */
export function BookingPaymentReturnPanel({
  outcome,
  bookingId,
  onDone,
}: BookingPaymentReturnPanelProps) {
  const { t } = useI18n();
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const autoDoneRef = useRef(false);
  const [phase, setPhase] = useState<'processing' | 'confirmed' | 'failed' | 'cancelled'>(() => {
    if (outcome === 'failure') {
      return 'failed';
    }
    if (outcome === 'cancel') {
      return 'cancelled';
    }
    return 'processing';
  });
  const [pollKey, setPollKey] = useState(0);

  useEffect(() => {
    if (outcome !== 'success' || !bookingId?.trim()) {
      if (outcome === 'success' && !bookingId?.trim()) {
        setPhase('failed');
      }
      return undefined;
    }

    const ac = new AbortController();
    setPhase('processing');
    void (async () => {
      const result = await pollBookingUntilPaymentConfirmed({
        bookingId: bookingId.trim(),
        signal: ac.signal,
      });
      if (ac.signal.aborted) {
        return;
      }
      if (result.status === 'confirmed') {
        clearPayplusPendingCheckout();
        setPhase('confirmed');
        return;
      }
      if (result.status === 'pending') {
        setPhase('processing');
      }
    })();

    return () => ac.abort();
  }, [outcome, bookingId, pollKey]);

  // Confirmed / cancel / failure: hand off once (no extra "Back to bookings" tap).
  useEffect(() => {
    if (phase === 'processing' || autoDoneRef.current) {
      return;
    }
    // Success path: wait until poll marks confirmed.
    if (outcome === 'success' && phase !== 'confirmed') {
      return;
    }
    autoDoneRef.current = true;
    onDoneRef.current({
      bookingId: bookingId?.trim() || null,
      confirmed: phase === 'confirmed',
    });
  }, [phase, bookingId, outcome]);

  const title =
    phase === 'confirmed'
      ? t('bookings.payplus.confirmedTitle')
      : phase === 'failed'
        ? t('bookings.payplus.failedTitle')
        : phase === 'cancelled'
          ? t('bookings.payplus.cancelledTitle')
          : t('bookings.payplus.processingTitle');

  const body =
    phase === 'confirmed'
      ? t('bookings.payplus.confirmedBody')
      : phase === 'failed'
        ? t('bookings.payplus.failedBody')
        : phase === 'cancelled'
          ? t('bookings.payplus.cancelledBody')
          : t('bookings.payplus.processingBody');

  // Terminal phases hand off automatically — keep a brief status while navigation runs.
  if (phase !== 'processing') {
    return (
      <div className={styles.panel} role="status" aria-live="polite">
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.body}>{body}</p>
      </div>
    );
  }

  return (
    <div className={styles.panel} role="status" aria-live="polite">
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.body}>{body}</p>
      <p className={styles.hint}>{t('bookings.payplus.processingHint')}</p>
      <button type="button" className={styles.doneBtn} onClick={() => setPollKey((n) => n + 1)}>
        {t('bookings.payplus.keepWaiting')}
      </button>
    </div>
  );
}
